import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { useLocation } from 'react-router';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import LoadingSpinner from '../../components/LoadingSpinner';
import Button from '../../components/Button';
import OrganisationLogo from '../../components/OrganisationLogo';

// ICONS
import { ReactComponent as ChecklistIcon } from '../../assets/svg/checklist.svg';

// TYPES
import { AccountParentVerificationCommand } from '../../api/models';

interface ParentConfirmTypes {}

const ParentConfirm: React.FC<ParentConfirmTypes> = ({}) => {
	// Get Website Url from Redux store
	const websiteUrl = useAppSelector((state) => state.lists.websiteUrl);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Confirm State on initial load
	const [isTermsConfirmed, setIsTermsConfirmed] = React.useState<boolean>(false);
	const [isPolicyConfirmed, setIsPolicyConfirmed] = React.useState<boolean>(false);

	// Parent Verification State
	const [isVerificationDone, setIsVerificationDone] = React.useState<boolean>(false);

	// Get token, email and organisationIdentifier from query string
	const { search } = useLocation();
	const token = new URLSearchParams(search).get('token');
	const email = new URLSearchParams(search).get('email');
	const organisationIdentifier = new URLSearchParams(search).get('organisationIdentifier');

	// State to store logo
	const [organisationLogo, setOrganisationLogo] = React.useState<string>('');

	// Get the Parent Verification Status on page load
	const getParentVerificationStatus = async () => {
		setIsLoading(true);

		const response = await apibridge.getParentVerificationStatus(
			token || '',
			email || '',
			organisationIdentifier || ''
		);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsVerificationDone(response.result.isVerified);

			setOrganisationLogo(response.result.organisationLogo);

			setIsLoading(false);
		}
	};

	React.useEffect(() => {
		getParentVerificationStatus();
	}, []);

	// On Submit
	const sendParentVerification = async () => {
		setIsLoading(true);

		const data: AccountParentVerificationCommand = {
			token: token || '',
			email: email || '',
			organisationIdentifier: organisationIdentifier || ''
		};

		const response = await apibridge.sendParentVerification(data);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsTermsConfirmed(true);

			setIsPolicyConfirmed(true);

			setIsLoading(false);
		}
	};

	const validationSchema = Yup.object({
		confirmTermsInput: Yup.bool().required('Required').oneOf([true], 'You must agree to continue'),
		confirmPolicyInput: Yup.bool().required('Required').oneOf([true], 'You must agree to continue')
	});

	const initialValues = {
		confirmTermsInput: isTermsConfirmed,
		confirmPolicyInput: isPolicyConfirmed
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={sendParentVerification}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="confirm-email-change-page">
						{!!formErrorMessage ? (
							<div className="form-validation-error">{formErrorMessage}</div>
						) : (
							<SwitchTransition mode="out-in">
								<CSSTransition
									key={`key_${isLoading}`}
									timeout={600}
									classNames="standalone-fade"
									mountOnEnter
									unmountOnExit
								>
									{isLoading ? (
										<LoadingSpinner show={isLoading} />
									) : (
										<div className="general-form-layout-wrapper center">
											<div className="form-main">
												<div className="check-email">
													{isTermsConfirmed === false || isPolicyConfirmed === false ? (
														<>
															<div className="header mbtom-30">
																<OrganisationLogo src={organisationLogo} alt="organisation logo" />
															</div>

															<h1>Supervision agreement requested</h1>

															<p className="sub-grey-text mtop-20 mbtom-60">
																Please review and accept the following to continue
															</p>

															<div className="confirm-container">
																<div className="form-group">
																	<label className="form-control-checkbox">
																		<Field
																			name="confirmTermsInput"
																			id="confirmTermsInput"
																			type="checkbox"
																			className="form-input"
																		/>

																		<span className="text-align-left">
																			I am the parent or legal guardian of a minor between 13 and 18 years of age and
																			agree to supervise their use of the ChildAware platform in accordance with the
																			Individual User{' '}
																			<a href={`${websiteUrl}/terms-conditions-individual-user`} target="_blank">
																				Terms and Conditions
																			</a>
																			.
																		</span>
																	</label>

																	<ErrorMessage
																		name="confirmTermsInput"
																		component="div"
																		className="error-message text-align-left"
																	/>
																</div>

																<div className="form-group">
																	<label className="form-control-checkbox">
																		<Field
																			name="confirmPolicyInput"
																			id="confirmPolicyInput"
																			type="checkbox"
																			className="form-input"
																		/>

																		<span className="text-align-left">
																			I have read and understood the{' '}
																			<a href={`${websiteUrl}/privacy-policy`} target="_blank">
																				Privacy Policy
																			</a>{' '}
																			which describes how my personal information and that of the minor I am supervising
																			may be handled by ChildAware.
																		</span>
																	</label>

																	<ErrorMessage
																		name="confirmPolicyInput"
																		component="div"
																		className="error-message text-align-left"
																	/>
																</div>

																<div className="form-button-group text-align-center">
																	<Button type="submit" className="btn-primary w-100">
																		Agree and continue
																	</Button>
																</div>
															</div>
														</>
													) : (
														<div className="confirm-container">
															<div className="header mbtom-30">
																<ChecklistIcon />
															</div>

															<div className="content">
																<h1>Thank you</h1>

																<br />

																<p>
																	Thank you – Your agreement for your child to create a ChildAware account has been
																	received. Your child will now receive an email to finalise their account creation and
																	will then be able to access their organisation’s child safety information through
																	ChildAware.
																</p>
															</div>
														</div>
													)}
												</div>
											</div>
										</div>
									)}
								</CSSTransition>
							</SwitchTransition>
						)}
					</Form>
				);
			}}
		</Formik>
	);
};

export default ParentConfirm;
